import { useQuery, useMutation } from '@tanstack/react-query'
import { RouteParamsFilterParams, CreateRouteParamParams, UpdateRouteParamParams } from '@/types/route-param'
import { useRouteParamsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useRouteParams = (params: RouteParamsFilterParams) => {
  const fetchRouteParams = useRouteParamsStore(state => state.fetchRouteParams)

  return useQuery({
    queryKey: ['routeParams', params],
    queryFn: () => fetchRouteParams(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useRouteParam = (messageId: string) => {
  const getRouteParam = useRouteParamsStore(state => state.getRouteParam)

  return useQuery({
    queryKey: ['routeParam', messageId],
    queryFn: () => getRouteParam(messageId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!messageId
  })
}

export const useCreateRouteParam = () => {
  const createRouteParam = useRouteParamsStore(state => state.createRouteParam)

  return useMutation({
    mutationFn: (params: CreateRouteParamParams) => createRouteParam(params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useUpdateRouteParam = () => {
  const updateRouteParam = useRouteParamsStore(state => state.updateRouteParam)

  return useMutation({
    mutationFn: ({ messageId, params }: { messageId: string; params: UpdateRouteParamParams }) =>
      updateRouteParam(messageId, params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useDeleteRouteParam = () => {
  const deleteRouteParam = useRouteParamsStore(state => state.deleteRouteParam)

  return useMutation({
    mutationFn: (messageId: string) => deleteRouteParam(messageId),
    onSuccess: () => {},
    throwOnError: false
  })
}

export const useBulkDeleteRouteParams = () => {
  const bulkDeleteRouteParams = useRouteParamsStore(state => state.bulkDeleteRouteParams)
  const deselectAllMessages = useRouteParamsStore(state => state.deselectAllMessages)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteRouteParams(ids),
    onSuccess: response => {
      deselectAllMessages()
      handleBulkDeleteSuccess(response)
    }
  })
}
